/**
 * Sprint 30 — Conversión de números a letras en español.
 *
 * Función PURA (sin red, sin DOM). Se usa para la confirmación del monto
 * inicial de caja: mostrar "$750.00 — Setecientos cincuenta pesos 00/100"
 * para que el dueño cache un error de dedo (750 vs 7500) antes de abrir.
 *
 * Cubre 0 .. 999,999,999 — más que suficiente para montos de un POS.
 */

const UNIDADES = [
  '',
  'uno',
  'dos',
  'tres',
  'cuatro',
  'cinco',
  'seis',
  'siete',
  'ocho',
  'nueve',
  'diez',
  'once',
  'doce',
  'trece',
  'catorce',
  'quince',
  'dieciséis',
  'diecisiete',
  'dieciocho',
  'diecinueve',
  'veinte',
  'veintiuno',
  'veintidós',
  'veintitrés',
  'veinticuatro',
  'veinticinco',
  'veintiséis',
  'veintisiete',
  'veintiocho',
  'veintinueve',
];

const DECENAS = [
  '',
  '',
  '',
  'treinta',
  'cuarenta',
  'cincuenta',
  'sesenta',
  'setenta',
  'ochenta',
  'noventa',
];

const CENTENAS = [
  '',
  'ciento',
  'doscientos',
  'trescientos',
  'cuatrocientos',
  'quinientos',
  'seiscientos',
  'setecientos',
  'ochocientos',
  'novecientos',
];

/**
 * Apócope de "uno": "veintiuno" → "veintiún", "...uno" → "...un".
 * Necesario antes de un sustantivo o de "mil"/"millones".
 */
function apocope(s: string): string {
  if (s.endsWith('veintiuno')) return s.slice(0, -'veintiuno'.length) + 'veintiún';
  if (s.endsWith('uno')) return s.slice(0, -'uno'.length) + 'un';
  return s;
}

/** Convierte 0..999 a palabras. */
function tresCifras(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'cien';
  const centena = Math.floor(n / 100);
  const resto = n % 100;
  let s = centena > 0 ? CENTENAS[centena] : '';
  if (resto > 0) {
    if (s) s += ' ';
    if (resto < 30) {
      s += UNIDADES[resto];
    } else {
      const decena = Math.floor(resto / 10);
      const unidad = resto % 10;
      s += DECENAS[decena];
      if (unidad > 0) s += ' y ' + UNIDADES[unidad];
    }
  }
  return s;
}

/**
 * Convierte un entero a palabras. `numeroALetras(7500)` → "siete mil quinientos".
 * Negativos se tratan por su valor absoluto; decimales se truncan.
 */
export function numeroALetras(num: number): string {
  const n = Math.floor(Math.abs(num));
  if (!Number.isFinite(n) || n === 0) return 'cero';

  const millones = Math.floor(n / 1_000_000);
  const miles = Math.floor((n % 1_000_000) / 1000);
  const resto = n % 1000;

  const partes: string[] = [];
  if (millones > 0) {
    partes.push(
      millones === 1 ? 'un millón' : `${apocope(tresCifras(millones))} millones`,
    );
  }
  if (miles > 0) {
    partes.push(miles === 1 ? 'mil' : `${apocope(tresCifras(miles))} mil`);
  }
  if (resto > 0) {
    partes.push(tresCifras(resto));
  }
  return partes.join(' ').trim();
}

/**
 * Convierte un monto a letras con formato contable.
 * `montoEnLetras(750.5)` → "Setecientos cincuenta pesos 50/100".
 * `montoEnLetras(1)`     → "Un peso 00/100".
 */
export function montoEnLetras(monto: number): string {
  const abs = Math.abs(Number.isFinite(monto) ? monto : 0);
  const entero = Math.floor(abs);
  const centavos = Math.round((abs - entero) * 100);
  // Por si el redondeo de centavos sube a 100.
  const enteroFinal = centavos === 100 ? entero + 1 : entero;
  const centavosFinal = centavos === 100 ? 0 : centavos;

  const palabras = apocope(numeroALetras(enteroFinal));
  const sustantivo = enteroFinal === 1 ? 'peso' : 'pesos';
  const cc = String(centavosFinal).padStart(2, '0');
  const frase = `${palabras} ${sustantivo} ${cc}/100`;
  return frase.charAt(0).toUpperCase() + frase.slice(1);
}
